import { useCallback, useEffect, useRef, useState } from "react";

const getFormattedData = (array, key) => {
  const gh = array.filter((fr) => fr.types.includes(key));
  return gh.length > 0 ? gh[0] : { long_name: "" };
};

// Default: San Salvador, El Salvador (LatAm focus)
const defaultCenter = {
  lat: 13.7942,
  lng: -88.8965,
};

const mapOptions = {
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  zoomControl: false,
  scaleControl: false,
  rotateControl: false,
  panControl: false,
};

const GoogleMapView = ({
  location,
  zoomlevel = 14,
  onClickAction,
  height = "300px",
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let timeoutId;
    const checkGoogleMapsLoaded = () => {
      if (window.google?.maps) {
        setIsLoaded(true);
      } else {
        timeoutId = setTimeout(checkGoogleMapsLoaded, 100);
      }
    };

    checkGoogleMapsLoaded();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const getReverseGeocodingData = useCallback(async (lat, lng) => {
    const geocoder = new window.google.maps.Geocoder();
    const { results } = await geocoder.geocode({ location: { lat, lng } });
    if (results.length > 0) {
      const placeDetails = results[0];
      const cityD = getFormattedData(
        placeDetails.address_components,
        "locality"
      );
      const stateD = getFormattedData(
        placeDetails.address_components,
        "administrative_area_level_1"
      );
      const zipD = getFormattedData(
        placeDetails.address_components,
        "postal_code"
      );
      return {
        address: placeDetails.formatted_address,
        city: cityD?.long_name,
        state: stateD?.long_name,
        zip: zipD?.long_name,
        lat,
        lng,
      };
    }
    return {};
  }, []);

  useEffect(() => {
    if (!isLoaded || !mapRef.current) {
      return;
    }

    const center =
      location?.lat && location?.lng
        ? { lat: location.lat, lng: location.lng }
        : defaultCenter;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: zoomlevel,
        ...mapOptions,
      });

      mapInstanceRef.current.addListener("click", async (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        const data = await getReverseGeocodingData(lat, lng);
        if (onClickAction) {
          onClickAction(data);
        }
      });
    } else {
      mapInstanceRef.current.setCenter(center);
      mapInstanceRef.current.setZoom(zoomlevel);
    }

    if (location?.lat && location?.lng) {
      const position = { lat: location.lat, lng: location.lng };
      if (!markerRef.current) {
        markerRef.current = new window.google.maps.Marker({
          map: mapInstanceRef.current,
          position,
          title: location.address || "",
        });
      } else {
        markerRef.current.setPosition(position);
        markerRef.current.setTitle(location.address || "");
      }
    } else if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
  }, [isLoaded, location, zoomlevel, getReverseGeocodingData, onClickAction]);

  if (!isLoaded) {
    return <div className="text-sm text-gray-500">Loading map...</div>;
  }

  return (
    <div
      ref={mapRef}
      style={{ width: "100%", height }}
      className="rounded-md overflow-hidden"
    />
  );
};

export default GoogleMapView;
